import { Injectable } from '@angular/core';
import { debounceTime, map } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';

import { ApiService } from '../api.service';
import { UserProfileService } from './user-profile.service';
import { AuthService } from './../auth.service';
import { environment } from '../../environments/environment';
import { UploadInput } from 'ngx-uploader';
import { HandleFileService } from './handle-file.service';
import { DataObject } from '../interfaces/data-objects';
import uuid from '../utils/uuid';
import { TranslateService } from './../api/translate.service';
import { CustomerLandingFilterObjInit } from '../interfaces/ag-grid-interfaces';
import {
  fieldCustomerIdCol,
  fieldCustomerNameCol,
  fieldAddressLine1Col,
  fieldCountryCol,
  fieldGstRateCol,
  fieldNewUpdateCol,
  fieldErrorCol,
  fieldPercentageCol,
  fieldABNCol
} from '../utils/ag-grid/column-defs'
import { ColDef } from 'ag-grid-community';

@Injectable({
  providedIn: 'root'
})
export class CustomerService {
  vatNumberSubject: BehaviorSubject<any> = new BehaviorSubject<any>('');
  modifyAccount: BehaviorSubject<any> = new BehaviorSubject<any>('');
  private isCustomerAlreadyExistsSubject = new BehaviorSubject<boolean>(false);

  isCustomerAlreadyExists$ = this.isCustomerAlreadyExistsSubject.asObservable();

  constructor(
    private api: ApiService,
    private handleFile: HandleFileService,
    private userProfileService: UserProfileService,
    private auth: AuthService,
    private translateService: TranslateService
  ) { }

  setIsCustomerAlreadyExists(value: boolean): void {
    this.isCustomerAlreadyExistsSubject.next(value);
  }

  getIsCustomerAlreadyExists(): boolean {
    return this.isCustomerAlreadyExistsSubject.value;
  }

  searchAll(query) {
    return this.api.get(`/api/ps/customers/?q=${encodeURIComponent(query)}`);
  }

  searchSoldTo(query) {
    return this.api.get(`/api/ps/customers/?sold_to=true&q=${encodeURIComponent(query)}`);
  }

  searchBillTo(query) {
    return this.api.get(`/api/ps/customers/?bill_to=true&q=${encodeURIComponent(query)}`);
  }

  getShippingCustomers() {
    return this.api.get(`/api/ps/customers/?ship_to=true&page_size=100`)
    .pipe(
      map(result => {
        return result.results;
      })
    );
  }

  getById(customerId: number) {
    return this.api.get(`/api/ps/customers/?id=${encodeURIComponent(customerId)}`);
  }

  getAddresses(query: string) {
    return this.api.get(`/api/ps/customers/?id=${encodeURIComponent(query)}&verbose=true`)
      .pipe(
        map(result => {
          return result.results[0]?.addresses?.addresses;
        })
      );
  }

  searchContacts(field, query, customer?) {
    let url = `/api/ps/contacts/?${field}=${encodeURIComponent(query)}`;
    if (customer) {
      url += `&customer_id=${customer}`;
    }
    return this.api.get(url)
      .pipe(
        map(result => {
          return result.results;
        })
      );
  }

  searchEmployees(query) {
    const url = `/api/ps/brokers/?name=${encodeURIComponent(query)}`;
    return this.api.get(url)
      .pipe(
        map(result => {
          return result.results;
        })
      );
  }

  getEmployee(employeeId: number) {
    const url = `/api/ps/brokers/?emplid=${employeeId}`;
    return this.api.get(url)
      .pipe(
        map(result => {
          return result.results[0];
        })
      );
  }

  getVATDetails(customerId) {
    return this.api.get(`/api/ps/vats/?cust_id=${customerId}`)
      .pipe(
        map(result => {
          return result.results;
        })
      );
  }

  createCustomer() {
    return this.api.post('/api/customer/', { broker: this.userProfileService.getEmployeeNumberLocalStorage() })
      .pipe(
        map(response => {
          if (response && response['id'] && response['hash']) {
            return response;
          }
        })
      );
  }

  validateClientABN(customerId) {
    return this.api.get(`/api/customer/${customerId}/client_abn_validate/`).pipe(
      map(response => response)
    );
  }

  submitCustomer(customerId: number, customerHash: string) {
    return this.api.post(`/api/customer/${customerId}/submit/?hash=${customerHash}`, {});
  }

  getCustomer(customerId: number, customerHash: string | null) {
    return this.api.get(`/api/customer/${customerId}/?hash=${customerHash}`)
    .pipe(
      map(response => {
        return response;
      })
    );
  }

  updateCustomer(customerId: number, customerHash: string | null, customer: DataObject) {
    return this.api.put(`/api/customer/${customerId}/?hash=${customerHash}`, customer)
    .pipe(
      debounceTime(500)
    );
  }

  deleteCustomer(customerId: number, customerHash: string | null) {
    return this.api.delete(`/api/customer/${customerId}/?hash=${customerHash}`);
  }

  getGlClients(query) {
    return this.api.get(`/api/customer/gl_client/?q=${encodeURIComponent(query)}`);
  }

  getCompanyTypes() {
    return this.api.get(`/api/customer/company_type/`)
    .pipe(
      map(response => {
        return response['options'];
      })
    );
  }

  getUpdateCustomer(customerId: number, customerHash: string) {
    return this.api.get(`/api/customer/customer_update/${customerId}/?hash=${customerHash}`)
    .pipe(
      map(response => {
        return response;
      })
    );
  }

  postUpdateCustomer() {
    return this.api.post(`/api/customer/customer_update/`, {})
    .pipe(
      map(response => {
        if (response && response['id'] && response['hash']) {
          return response;
        }
      })
    );
  }

  putUpdateCustomer(customerId: number, customerHash: string, customer: DataObject) {
    return this.api.put(`/api/customer/customer_update/${customerId}/?hash=${customerHash}`, customer)
    .pipe(
      debounceTime(500)
    );
  }

  addUpdateAddress(address) {
    return this.api.post(`/api/customer/address/`, address)
    .pipe(
      map(response => {
        return response;
      })
    );
  }
  getUpdatedAddress(id) {
    return this.api.get(`/api/customer/address/${id}`)
    .pipe(
      map(response => {

        return response;
      })
    );
  }

  submitUpdateCustomer(customerId: number, customerHash: string) {
    return this.api.post(`/api/customer/customer_update/${customerId}/submit/?hash=${customerHash}`, {});
  }

  updateAddress(addid, address) {
    return this.api.put(`/api/customer/address/${addid}/`, address)
    .pipe(
      debounceTime(500)
    );
  }

  deleteAddress(addid) {
    return this.api.delete(`/api/customer/address/${addid}/`);
  }

  // TODO: Remove this function and implement the new pure pipe customer-name-fn.pipe
  getCustomerName(customer: DataObject, separator: string = ' '): string {
    if (customer) {
      const customerName = [customer.name1 || '', customer.name2 || '', customer.name3 || ''];
      return customerName.filter(name => name !== '' && name !== ' ').join(separator);
    }
    return '';
  }

  getCustomerProperties(customerId) {
    return this.api.get(`/api/customer/${customerId}/customer_address/`)
    .pipe(
      map(response => {
        return response;
      })
    );
  }

  createCustomerProperty(customerId) {
    return this.api.post(`/api/customer/${customerId}/customer_address/`, {})
    .pipe(
      map(response => {
        return response;
      })
    );
  }

  updateCustomerProperty(customerId, property) {
    return this.api.put(`/api/customer/${customerId}/customer_address/${property.id}/`, property)
    .pipe(
      debounceTime(500)
    );
  }

  deleteCustomerProperty(customerId, propertyId) {
    return this.api.delete(`/api/customer/${customerId}/customer_address/${propertyId}`);
  }

  uploadApprovalDocument(customerId, customerHash) {
    const authHeaders = {};
    const mockToken = environment.mockToken;
    const jwtToken = this.auth.getAuthToken();

    if (mockToken && mockToken !== '') {
      authHeaders['Authorization'] = 'Token ' + mockToken;
    } else if (jwtToken) {
      authHeaders['Authorization'] = 'JWT ' + jwtToken;
    }

    const event: UploadInput = {
      type: 'uploadAll',
      url: this.api.getPortwithURL(`/api/customer/${customerId}/upload/?hash=${customerHash}`),
      method: 'POST',
      headers: authHeaders
    };

    return event;
  }

  deleteApprovalDocument(fileId) {
    return this.api.delete(`/api/customer/upload/${fileId}/`);
  }

  downloadCustomerDocument(customerId, { id, file_name }) {
    return this.handleFile.get(`/api/customer/${customerId}/download/${id}/`)
    .pipe(
      map(response => {
        if (response) {
          const contentType = this.handleFile.getContentType(file_name);
          return new Blob([response], { 'type': contentType });
        }
      })
    );
  }

  deleteUpdateCustomer(customerId: number, customerHash: string) {
    return this.api.delete(`/api/customer/customer_update/${customerId}/?hash=${customerHash}`);
  }

  getCustomerLandingTopSection() {
    return this.api.get(`/api/landing_customer/top_section/`)
    .pipe(
      map(response => {
        return response.top_section;
      })
    );
  }

  getCustomerLandingSubSection(status) {
    return this.api.get(`/api/landing_customer/sub_section/?status=${status}`)
    .pipe(
      map(response => {
        return response.sub_section;
      })
    );
  }

  setVatNumber(vatNumber) {
    this.vatNumberSubject.next(vatNumber);
  }

  searchEDPCompany(query, countries) {
    // stringifying the query to handle special characters in the query string like '&' and '=' etc.
    const stringifyQuery = encodeURIComponent(query);
    let url = `/api/transactions/search_company_name_es/?name=${stringifyQuery}&countries=`;
    url += countries?.length > 0 ? `${encodeURIComponent(JSON.stringify(countries))}`: `all`;
    return this.api.get(url);
  }

  getByEDPCompanyId(query) {
    return this.api.get(`/api/transactions/get_edp_company_name/?edp_company_id=${query}`);
  }

  searchEDPContact(query) {
    return this.api.get(`/api/transactions/search_contact_name_es/?contact=${query}`);
  }

  getByEDPContactId(query) {
    return this.api.get(`/api/transactions/get_edp_contact_name/?edp_contact_id=${query}`);
  }

  updateAccount(obj) {
    this.modifyAccount.next(obj);
  }

  getCustomerLandingData(
    querySearch?: string,
    page: number = 1,
    page_size: number = 10,
    status?: string,
    sortBy?: string,
    sortDirection?: string,
    uid?: string) {
    let params = `page=${page}&page_size=${page_size}`;
    if (status) {
      params += `&status=${status}`;
    }
    if (querySearch) {
      params += `&q=${querySearch}`;
    }
    if (sortBy) {
      params += `&sort_by=${sortBy}`;
    }
    if (sortDirection) {
      params += `&sort_dir=${sortDirection}`;
    }
    if (uid) {
      params += `&uid=${uid}`;
    }
    return this.api.get(`/api/landing_customer?${params}`)
      .pipe(
        map(response => {
          return response;
        })
      );
  }

  getCustomerLandingQueryParams() {
    return {
      page: CustomerLandingFilterObjInit.paginationPage,
      page_size: CustomerLandingFilterObjInit.paginationPageSize,
      uid: uuid()
    };
  }

  getCustomerLandingSettings(data: { status: string, userCountry: string }): ColDef[] {
    return [
      {
        ...fieldCustomerIdCol,
        headerName: this.translateService.getTranslation('CUSTOMER_ID')
      },
      {
        ...fieldCustomerNameCol,
        headerName: this.translateService.getTranslation('CUSTOMER_NAME'),
        valueGetter: params => {
          if (params.data?.company_name) {
            return params.data.company_name;
          } else if (params.data?.customer_name) {
            return params.data.customer_name;
          }
          return '';
        }
      },
      {
        ...fieldAddressLine1Col,
        headerName: this.translateService.getTranslation('ADDRESS'),
        field: 'address',
        cellRenderer: 'propertyDataRenderer',
        tooltipValueGetter: undefined
      },
      {
        ...fieldCountryCol,
        headerName: this.translateService.getTranslation('COUNTRY'),
        valueGetter: params => params.data?.address?.country ? params.data.address.country : ''
      },
      {
        ...fieldABNCol,
        headerName: this.translateService.getTranslation('ABN'),
        hide: data.userCountry === 'AUS' ? false : true,
      },
      {
        headerName: this.translateService.getTranslation('CLIENT_LEGAL_NUMBER'),
        field: 'client_legal_entity_registration_number',
        hide: data.userCountry === 'JPN' ? false : true,
      },
      {
        headerName: this.translateService.getTranslation('CLIENT_VAT_NUMBER'),
        field: 'vat_registration_number',
        hide: data.userCountry === 'TWN' ? false : true,
      },
      {
        headerName: this.translateService.getTranslation('CLIENT_VAT_NUMBER'),
        field: 'client_vat_registration_number',
        hide: ['KOR', 'THA', 'PHL', 'VNM'].includes(data.userCountry) ? false : true,
        valueGetter: params => {
          const val = params.data?.client_vat_registration_number;
          return val
            ? `${val.substring(0, 3)}-${val.substring(3, 5)}-${val.substring(5)}`
            : '';
        }
      },
      {
        ...fieldGstRateCol,
        headerName: this.translateService.getTranslation('GSTIN'),
        field: 'address.goods_services_tax_identification_number',
        hide: data.userCountry === 'IND' ? false : true,
        valueGetter: params => {
          const gstin = params.data?.address?.goods_services_tax_identification_number;
          return gstin ? gstin : '';
        }
      },
      {
        ...fieldNewUpdateCol,
        headerName: this.translateService.getTranslation('NEWORUPDATE'),
        valueGetter: params => params.data?.key ? params.data.key.toUpperCase() : ''
      },
      {
        ...fieldErrorCol,
        headerName: this.translateService.getTranslation('ERROR'),
        cellRenderer: 'customerErrorRenderer',
        tooltipValueGetter: undefined
      },
      {
        ...fieldPercentageCol,
        headerName: this.translateService.getTranslation('STATUS'),
        field: 'ps_status',
        cellRenderer: undefined,
      },
      {
        headerName: '',
        cellRendererSelector: (params) => {
          const status = params.data?.status?.toLowerCase();
          if (status === 'work in progress' || status === 'wip') {
            return { component: 'deleteCustomerRenderer' };
          }
          return undefined; // hide cell renderer
        },
        hide: data.status.toLocaleLowerCase() !== 'submitted' ? false : true,
      }
    ] 
  }

  getToggledAddressKey(address: DataObject) {
    let toggleKey = '';
    if (address.id) { // New addresses will have 'id' field
      toggleKey = `id-${address.id.toString()}`; 
    } else if (address.address_seq_num) { // Existing PS addresses will have 'address_seq_num' field
      toggleKey = `seq-num-${address.address_seq_num.toString()}`;
    }
    return toggleKey;
  }

}
